import {
  Damage,
  Inspection,
  PreTimeBand,
  PreTimeSuggestionItem,
  PreTimeSuggestionLine,
} from '@/types';
import { suggestionItemKey } from './preTimeSuggestionApply';

export const PRE_TIME_LOOKBACK_YEARS = 2;

export const bandLabelKey = {
  HighConfidence: 'preTimeHighConfidence',
  NeedReview: 'preTimeNeedReview',
  Manual: 'preTimeManual',
} as const satisfies Record<PreTimeBand, string>;

export const usagePercent = (usedOn: number, sampleSize: number) =>
  sampleSize > 0 ? Math.round((usedOn / sampleSize) * 100) : 0;

export const countBands = (lines: PreTimeSuggestionLine[]) => ({
  HighConfidence: lines.filter((line) => line.band === 'HighConfidence').length,
  NeedReview: lines.filter((line) => line.band === 'NeedReview').length,
  Manual: lines.filter((line) => line.band === 'Manual').length,
});

export const itemsOfKind = (
  items: PreTimeSuggestionItem[],
  kinds: PreTimeSuggestionItem['kind'][],
) => items.filter((item) => kinds.includes(item.kind));

export type WhereUsedRow = {
  labelKey:
    | 'preTimeAttributeCustomer'
    | 'preTimeMakeModel'
    | 'preTimeAttributeDamageArea'
    | 'preTimeAttributeDamageType'
    | 'preTimeAttributeRepairAction';
  value: string;
  count: number;
  sampleSize: number;
};

export const whereUsedRows = (
  line: PreTimeSuggestionLine,
  damage: Damage | undefined,
  inspection: Pick<Inspection, 'customerName' | 'manufacturer' | 'model'>,
): WhereUsedRow[] => {
  const matched = new Set(line.matchedAttributes);
  const sampleSize = line.sampleSize;
  const rows: WhereUsedRow[] = [];
  const push = (labelKey: WhereUsedRow['labelKey'], value?: string | null) => {
    const text = value?.trim();
    if (!text) return;
    rows.push({ labelKey, value: text, count: sampleSize, sampleSize });
  };

  if (matched.has('customer'))
    push('preTimeAttributeCustomer', inspection.customerName);
  if (matched.has('make') || matched.has('model')) {
    push(
      'preTimeMakeModel',
      [inspection.manufacturer, inspection.model].filter(Boolean).join(' '),
    );
  }
  if (matched.has('damageArea')) {
    push('preTimeAttributeDamageArea', damage?.damageAreaDesc);
  }
  if (matched.has('damageType')) {
    push('preTimeAttributeDamageType', damage?.damageTypeDesc);
  }
  if (matched.has('repairAction')) {
    push('preTimeAttributeRepairAction', damage?.repairActionDesc);
  }
  return rows;
};

export const companionItems = (
  line: PreTimeSuggestionLine,
  item: PreTimeSuggestionItem,
) => {
  const key = suggestionItemKey(item);
  return line.items.filter((other) => suggestionItemKey(other) !== key);
};

export const itemLabel = (item: PreTimeSuggestionItem) =>
  item.description ? `${item.code} - ${item.description}` : item.code;
